import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SelectField,
  Text,
} from "@chakra-ui/react";
import { VARIANT } from "../../../constants/colors";
import jwt_decode from "jwt-decode";
import { JWTToken } from "../../../routes/privateRoute";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import SimulatorAPI from "../../../services/simulatorAPI";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import SystemApi from "../../../services/systemApi";
import ProductAPI from "../../../services/productAPI";
import { customColors } from "../../../atoms/customColors";
import { useRecoilState } from "recoil";

interface Flag {
  id: number;
  bandeira: string;
}

interface Product {
  id: number;
  nome: string;
}

export default function SalesSimulator({ isOpen, setIsOpen }: any) {
  const token = window.localStorage.getItem("token");
  let jwtToken: JWTToken = jwt_decode(token || "");
  const [flags, setFlags] = useState<Flag[]>([]);
  const [produtos, setProdutos] = useState<Product[]>([]);
  const [valor, setValor] = useState(0);
  const [calculo, setCalculo] = useState({
    vlReverse: false,
    valor: 0,
    idVenda: 0,
    idForma: "",
    idBandeira: 0,
  });
  const [simulation, setSimulation] = useState({
    valorLiquidoCliente: 0,
    valorTaxaCliente: 0,
  });

  const colors = useRecoilState(customColors)[0];

  const schema = yup
    .object({
      idBandeira: yup.number(),
      idVenda: yup.number(),
      idForma: yup.string().when("idVenda", {
        is: (value: number) => value === 4 || value === 11 || value === 12,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema.required("Forma de pagamento deve ser preenchida"),
      }),
      valor: yup.number(),
    })
    .required();

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      idVenda: 0,
      idForma: "",
      idBandeira: 0,
      valor: 0,
    },
  });

  const idVendaValue = watch("idVenda");

  useEffect(() => {
    if (isOpen) {
      getFlags();
      getProductsTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    if (flags.length > 0 && idVendaValue) {
      if (idVendaValue === 4 || idVendaValue === 11) {
        const pixFlag = flags.find((flag) => flag.bandeira === "PIX");
        if (pixFlag) {
          setCalculo((prev) => ({
            ...prev,
            idBandeira: pixFlag.id,
            idForma: "P",
          }));
          setValue("idBandeira", pixFlag.id);
          setValue("idForma", "P");
        } else {
          toast.error("Bandeira PIX não encontrada.");
        }
      } else if (idVendaValue === 12) {
        const bolePixFlag = flags.find((flag) => flag.bandeira === "BolePix");
        if (bolePixFlag && bolePixFlag.id === 15) {
          setCalculo((prev) => ({
            ...prev,
            idBandeira: 15,
            idForma: "BP",
          }));
          setValue("idBandeira", 15);
          setValue("idForma", "BP");
        } else {
          toast.error("Bandeira BolePix não encontrada.");
          setCalculo((prev) => ({
            ...prev,
            idBandeira: 0,
            idForma: "",
          }));
          setValue("idBandeira", 0);
          setValue("idForma", "");
        }
      }
    }
  }, [idVendaValue, flags, setValue]);

  async function getFlags() {
    try {
      const response = await SystemApi.getBandeiras(1, 100, "");
      setFlags(response as Flag[]);
    } catch (error) {
      console.log("error", error);
      toast.error("Erro ao carregar bandeiras.");
    }
  }

  async function getProductsTypes() {
    try {
      const response = await ProductAPI.getProducts(1, 100, "");
      setProdutos(response as Product[]);
    } catch (error) {
      console.log("error", error);
      toast.error("Erro ao carregar produtos.");
    }
  }

  function onClose() {
    setIsOpen(false);
    setCalculo({
      vlReverse: true,
      valor: 0,
      idVenda: 0,
      idForma: "",
      idBandeira: 0,
    });
    setSimulation({
      valorLiquidoCliente: 0,
      valorTaxaCliente: 0,
    });
    setValor(0);
  }

  function onSubmit(obj: any) {
    const objeto = {
      idCliente: Number(jwtToken.idCliente),
      idBandeira: obj.idBandeira,
      valor: Number(unMask(obj.valor)),
      vlReverse: obj.vlReverse,
      idProduto:
        obj.idVenda === 10
          ? 1
          : obj.idVenda === 11
            ? 1
            : obj.idVenda === 12
              ? 1
              : obj.idVenda,
      idForma: obj.idForma,
    };

    objeto.idProduto === 0
      ? setError("idVenda", { message: "Necessário preencher tipo de venda!" })
      : setError("idVenda", { message: undefined });

    objeto.idBandeira === 0
      ? setError("idBandeira", { message: "Necessário preencher a bandeira!" })
      : setError("idBandeira", { message: undefined });

    objeto.valor === 0
      ? setError("valor", { message: "Necessário preencher o valor!" })
      : setError("valor", { message: undefined });

    objeto.idForma === ""
      ? setError("idForma", { message: "Necessário preencher as parcelas!" })
      : setError("idForma", { message: undefined });

    if (
      errors.idBandeira?.message === undefined &&
      errors.idVenda?.message === undefined &&
      errors.valor?.message === undefined
    ) {
      calculate(objeto);
    }
  }

  const getReceivingTime = () => {
    if (calculo.idForma === "P") {
      return "D+0";
    } else if (calculo.idVenda === 2) {
      return "D+10";
    } else {
      return "D+1";
    }
  };

  const unMask = (value: any) => {
    const valorSemSimbolo = value.replace("R$ ", "");
    const valorSemPontos = valorSemSimbolo.replace(",", ".");
    return parseFloat(valorSemPontos);
  };

  async function calculate(obj: any) {
    try {
      const response = await SimulatorAPI.getSimulation(
        obj.idCliente,
        obj.idProduto,
        obj.idBandeira,
        obj.idForma,
        obj.valor,
        obj.vlReverse
      );
      setValor(Number(obj.valor));
      setSimulation(response);
    } catch (error: any) {
      toast.error("Erro ao calcular ou cliente não possui taxa cadastrada.");
      console.log(error);
    }
  }

  function getParcelas(qtde: number) {
    let parcelas: Array<{ id: string; number: string }> = [];

    for (let i = 1; i <= qtde; i++) {
      if (qtde === 1) {
        return <option value={"D"}>No débito</option>;
      }

      if (qtde > 24) {
        if (i < 10 && i > 1) {
          parcelas.push({
            id: `C0${i - 1}`,
            number: `No crédito ${i - 1}x`,
          });
        } else if (i === 1) {
          parcelas.push({
            id: `D`,
            number: `No débito`,
          });
        } else {
          parcelas.push({
            id: `C${i - 1}`,
            number: `No crédito ${i - 1}x`,
          });
        }
      } else {
        if (i < 10) {
          parcelas.push({
            id: `C0${i}`,
            number: `No crédito ${i}x`,
          });
        } else {
          parcelas.push({
            id: `C${i}`,
            number: `No crédito ${i}x`,
          });
        }
      }
    }

    return parcelas.map((parcela) => (
      <option key={parcela.id} value={parcela.id}>
        {parcela.number}
      </option>
    ));
  }

  function setVendaReversa() {
    const objeto = {
      vlReverse: true,
      valor: calculo.valor,
      idVenda: calculo.idVenda,
      idForma: calculo.idForma,
      idBandeira: calculo.idBandeira,
    };
    setCalculo((c) => ({ ...c, vlReverse: true }));
    setSimulation({
      valorLiquidoCliente: 0,
      valorTaxaCliente: 0,
    });
    setValor(0);
    onSubmit(objeto);
  }

  function setVendaDireta() {
    const objeto = {
      vlReverse: false,
      valor: calculo.valor,
      idVenda: calculo.idVenda,
      idForma: calculo.idForma,
      idBandeira: calculo.idBandeira,
    };
    setCalculo((c) => ({ ...c, vlReverse: false }));
    setSimulation({
      valorLiquidoCliente: 0,
      valorTaxaCliente: 0,
    });
    setValor(0);
    onSubmit(objeto);
  }

  return (
    <>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={onClose}
        size={"3xl"}
      >
        <ModalOverlay bgColor={"#ffffff83"} />
        <ModalContent
          borderRadius={"20px"}
          bgColor={colors.BLUEMONEDDBUTTONS}
          flexDir={"row"}
          color={"white"}
        >
          <ModalCloseButton />
          <ModalBody
            h={"75vh"}
            bgColor={colors.BLUEMONEDDBACKGROUND}
            borderRadius={"20px 0 0 20px"}
            display={"flex"}
            flexDir={"column"}
            justifyContent={"space-evenly"}
            alignItems={"center"}
          >
            <Flex
              w={"100%"}
              h={"50px"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Text fontWeight={"bold"} fontSize={"1.5rem"}>
                SIMULE UMA VENDA
              </Text>
            </Flex>
            <Flex
              w={"100%"}
              h={"50px"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Button
                bg={"none"}
                bgColor={
                  calculo.vlReverse ? colors.GRAY : colors.BLUEMONEDDBUTTONS
                }
                borderRadius={"none"}
                _hover={{ bgColor: VARIANT }}
                w={"45%"}
                borderRight={"1px solid white"}
                onClick={() => setVendaDireta()}
              >
                DIRETA
              </Button>
              <Button
                bg={"none"}
                bgColor={
                  !calculo.vlReverse ? colors.GRAY : colors.BLUEMONEDDBUTTONS
                }
                _hover={{ bgColor: VARIANT }}
                borderRadius={"none"}
                w={"45%"}
                onClick={() => setVendaReversa()}
              >
                REVERSA
              </Button>
            </Flex>
            <Flex
              w={"100%"}
              flexDir={"column"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <FormLabel>Digite o valor da venda</FormLabel>
              <NumericFormat
                decimalSeparator=","
                decimalScale={2}
                customInput={Input}
                value={calculo.valor}
                w={"90%"}
                allowNegative={false}
                prefix={"R$ "}
                bgColor={"white"}
                borderRadius={"20px"}
                placeholder={"valor"}
                color={"black"}
                onChange={(e: any) =>
                  setCalculo({ ...calculo, valor: e.target.value })
                }
              />
              <Text color="red">{errors.valor?.message}</Text>
            </Flex>
            <Flex
              w={"100%"}
              flexDir={"column"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <FormLabel>Tipo de venda</FormLabel>
              <SelectField
                w={"90%"}
                borderRadius={"20px"}
                h={"40px"}
                px={4}
                value={calculo.idVenda}
                color={"black"}
                {...register("idVenda", {
                  required: true,
                  onChange: (e: any) => {
                    const selectedIdVenda = Number(e.target.value);
                    setCalculo((prev) => ({
                      ...prev,
                      idVenda: selectedIdVenda,
                      idForma:
                        selectedIdVenda === 4 || selectedIdVenda === 11
                          ? "P"
                          : selectedIdVenda === 12
                            ? "BP"
                            : "",
                      idBandeira:
                        selectedIdVenda === 4 || selectedIdVenda === 11
                          ? flags.find((f) => f.bandeira === "PIX")?.id || 0
                          : selectedIdVenda === 12
                            ? 15
                            : 0,
                    }));
                    if (selectedIdVenda === 4 || selectedIdVenda === 11) {
                      setValue("idForma", "P");
                      setValue(
                        "idBandeira",
                        flags.find((f) => f.bandeira === "PIX")?.id || 0
                      );
                    } else if (selectedIdVenda === 12) {
                      setValue("idForma", "BP");
                      setValue("idBandeira", 15);
                    }
                  },
                })}
              >
                <option value={0} selected disabled>
                  Selecione...
                </option>
                {produtos &&
                  produtos.map((product: any) => {
                    if (!product.nome.includes("Recorrencia")) {
                      return (
                        <option key={product.id} value={product.id}>
                          {product.nome}
                        </option>
                      );
                    }
                  })}
                <option value={"10"}>Venda Boleto</option>
                <option value={"11"}>Venda PIX</option>
                <option value={"12"}>Venda BolePix</option>
              </SelectField>
              <Text color="red">{errors.idVenda?.message}</Text>
            </Flex>
            <Flex
              w={"100%"}
              flexDir={"column"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <FormLabel>Bandeira</FormLabel>
              <SelectField
                w={"90%"}
                borderRadius={"20px"}
                h={"40px"}
                px={4}
                value={calculo.idBandeira}
                color={"black"}
                {...register("idBandeira", {
                  required: true,
                  onChange: (e: any) =>
                    setCalculo({
                      ...calculo,
                      idBandeira: Number(e.target.value),
                    }),
                })}
                disabled={
                  calculo.idVenda === 11 ||
                  calculo.idVenda === 4 ||
                  calculo.idVenda === 12
                }
              >
                <option value={0} selected disabled>
                  Selecione...
                </option>
                {calculo.idVenda === 12 ? (
                  <option value={15}>BolePix</option>
                ) : (
                  flags &&
                  flags
                    .filter((flag: any) => {
                      if (
                        calculo.idVenda === 10 &&
                        flag.bandeira === "Boleto"
                      ) {
                        return true;
                      }
                      if (
                        (calculo.idVenda === 11 || calculo.idVenda === 4) &&
                        flag.bandeira === "PIX"
                      ) {
                        return true;
                      }
                      if (
                        calculo.idVenda !== 10 &&
                        calculo.idVenda !== 11 &&
                        calculo.idVenda !== 4 &&
                        calculo.idVenda !== 12
                      ) {
                        if (
                          flag.bandeira !== "Boleto" &&
                          flag.bandeira !== "PIX" &&
                          flag.bandeira !== "BolePix"
                        ) {
                          return true;
                        }
                      }
                      return false;
                    })
                    .map((flag: any) => (
                      <option key={flag.id} value={flag.id}>
                        {flag.bandeira}
                      </option>
                    ))
                )}
              </SelectField>
              <Text color="red">{errors.idBandeira?.message}</Text>
            </Flex>
            <Flex
              w={"100%"}
              flexDir={"column"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <FormLabel>Forma de Pagamento</FormLabel>
              <SelectField
                w={"90%"}
                borderRadius={"20px"}
                h={"40px"}
                px={4}
                value={calculo.idForma}
                color={"black"}
                {...register("idForma", {
                  onChange: (e: any) =>
                    setCalculo({ ...calculo, idForma: e.target.value }),
                })}
                disabled={
                  calculo.idVenda === 11 ||
                  calculo.idVenda === 4 ||
                  calculo.idVenda === 12
                }
              >
                <option value={""} selected disabled>
                  Selecione...
                </option>
                {calculo.idVenda === 10 ? (
                  <option value={"B"}>À vista no Boleto</option>
                ) : calculo.idVenda === 11 || calculo.idVenda === 4 ? (
                  <option value={"P"}>À vista no PIX</option>
                ) : calculo.idVenda === 12 ? (
                  <option value={"BP"}>À vista no BolePix</option>
                ) : calculo.idBandeira === 1 ||
                  calculo.idBandeira === 2 ||
                  calculo.idBandeira === 3 ||
                  calculo.idBandeira === 11 ||
                  calculo.idBandeira === 12 ||
                  calculo.idBandeira === 8 ||
                  calculo.idBandeira === 9 ? (
                  getParcelas(21)
                ) : calculo.idBandeira === 5 || calculo.idBandeira === 6 ? (
                  getParcelas(1)
                ) : (
                  getParcelas(21)
                )}
              </SelectField>
              <Text color="red">{errors.idForma?.message}</Text>
            </Flex>
            <Flex
              w={"100%"}
              flexDir={"column"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Button
                w={"90%"}
                bgColor={colors.BLUEMONEDDBUTTONS}
                borderRadius={"none"}
                _hover={{ bgColor: VARIANT }}
                isDisabled={
                  calculo.idBandeira === 0 ||
                  calculo.idVenda === 0 ||
                  calculo.valor === 0 ||
                  (calculo.idVenda !== 4 &&
                    calculo.idVenda !== 11 &&
                    calculo.idVenda !== 12 &&
                    calculo.idForma === "")
                }
                onClick={handleSubmit(() => onSubmit(calculo))}
              >
                CALCULAR
              </Button>
            </Flex>
          </ModalBody>
          <ModalBody
            bgColor={colors.BLUEMONEDDBUTTONS}
            borderRadius={"0 20px 20px 0"}
            display="flex"
            alignItems={"center"}
            justifyContent={"center"}
            flexDir={"column"}
          >
            <Flex
              alignItems={"center"}
              justifyContent={"space-evenly"}
              h={"60%"}
              flexDir={"column"}
            >
              <Flex
                flexDir={"column"}
                justifyContent="center"
                alignItems={"center"}
              >
                <Text fontWeight={"bold"} fontSize={"1.5rem"}>
                  RESULTADO
                </Text>
                {calculo.vlReverse ? (
                  <Text fontWeight={"bold"} fontSize={"1.5rem"}>
                    VENDA REVERSA
                  </Text>
                ) : (
                  <Text fontWeight={"bold"} fontSize={"1.5rem"}>
                    VENDA DIRETA
                  </Text>
                )}
              </Flex>
              {calculo.vlReverse ? (
                <Text>
                  Para receber{" "}
                  {valor &&
                    valor.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  , você precisa vender:
                </Text>
              ) : (
                <Text>
                  Se vender{" "}
                  {valor &&
                    valor.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  , você recebe:
                </Text>
              )}
              <Flex
                w={"280px"}
                h={"150px"}
                bgColor={colors.BLUEMONEDDINFOMODAL}
                borderRadius={"20px"}
                alignItems={"center"}
                justifyContent={"center"}
                flexDir={"column"}
              >
                {calculo.idForma === "B" ? (
                  <Text>No boleto à vista</Text>
                ) : calculo.idForma === "P" ? (
                  <Text>No PIX</Text>
                ) : calculo.idForma === "BP" ? (
                  <Text>No BolePix</Text>
                ) : calculo.idForma === "D" ? (
                  <Text>No débito</Text>
                ) : calculo.idForma === "C01" ? (
                  <Text>No crédito à vista</Text>
                ) : calculo.idForma === "C10" || calculo.idForma === "C20" ? (
                  <Text>
                    No crédito em {calculo.idForma.replace("C", "")} X
                  </Text>
                ) : (
                  <Text>
                    No crédito em{" "}
                    {calculo.idForma.replace("C", "").replace("0", "")} X
                  </Text>
                )}
                <Text fontWeight={"bold"} fontSize={"2.5rem"}>
                  {simulation.valorLiquidoCliente.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Text>
                <Text>
                  Taxa de{" "}
                  {simulation.valorTaxaCliente.toFixed(2).replace(".", ",")}%
                </Text>
              </Flex>
              <Text>Taxas com antecipação de acordo com seu plano de recebimento.</Text>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
